export function nameToAttributeKey(name) {
  const accentMap = {
    á: 'a', à: 'a', ã: 'a', â: 'a', ä: 'a',
    é: 'e', è: 'e', ê: 'e', ë: 'e',
    í: 'i', ì: 'i', î: 'i', ï: 'i',
    ó: 'o', ò: 'o', ô: 'o', õ: 'o', ö: 'o',
    ú: 'u', ù: 'u', û: 'u', ü: 'u',
    ç: 'c', ñ: 'n',
  };
  let out = '';
  let lastSep = false;
  const trimmed = String(name ?? '').trim();
  for (const ch of trimmed) {
    const lower = ch.toLowerCase();
    if (accentMap[lower]) {
      out += accentMap[lower];
      lastSep = false;
    } else if (/[a-z0-9]/.test(lower)) {
      out += lower;
      lastSep = false;
    } else if ((ch === ' ' || ch === '-' || ch === '_') && !lastSep) {
      out += '_';
      lastSep = true;
    }
  }
  return out.replace(/^_+|_+$/g, '');
}

export function normalizeOptionsDraft(options) {
  return (options || [])
    .map((opt) => ({
      id: opt.id,
      key: String(opt.key ?? '').trim(),
      name: String(opt.name ?? '').trim(),
      values: (opt.values || []).map((v) => String(v).trim()).filter(Boolean),
    }))
    .filter((opt) => opt.name && opt.values.length > 0);
}

export function detectNewOptions(initialOptions, draftOptions) {
  const initialByKey = new Map();
  const initialByName = new Map();
  (initialOptions || []).forEach((opt) => {
    const key = String(opt.key ?? '').trim();
    const name = String(opt.name ?? '').trim().toLowerCase();
    if (key) initialByKey.set(key, opt);
    if (name) initialByName.set(name, opt);
  });

  return normalizeOptionsDraft(draftOptions)
    .filter((opt) => {
      if (opt.key && initialByKey.has(opt.key)) return false;
      if (initialByName.has(opt.name.toLowerCase())) return false;
      return true;
    })
    .map((opt) => ({
      ...opt,
      key: opt.key || nameToAttributeKey(opt.name),
    }));
}

export function optionsNeedExistingSkuDefaults(initialOptions, draftOptions, variants) {
  if (!variants?.length) return false;
  return detectNewOptions(initialOptions, draftOptions).length > 0;
}

export function buildDefaultValuesPayload(newOptions, selectedDefaults = {}) {
  const payload = {};
  (newOptions || []).forEach((opt) => {
    const key = opt.key || nameToAttributeKey(opt.name);
    if (!key) return;
    const chosen = String(selectedDefaults[key] ?? selectedDefaults[opt.name] ?? '').trim();
    if (chosen) payload[key] = chosen;
  });
  return payload;
}

export function inferDefaultSelections(newOptions, prefill = {}) {
  const out = {};
  (newOptions || []).forEach((opt) => {
    const key = opt.key || nameToAttributeKey(opt.name);
    const fromPrefill =
      String(prefill[key] ?? '').trim() ||
      String(prefill[opt.name] ?? '').trim() ||
      String(opt.values?.[0] ?? '').trim();
    if (key && fromPrefill) out[key] = fromPrefill;
  });
  return out;
}

export function detectNewDraftAttributes(catalogAttributes, draftAttributes) {
  const catalogKeys = new Set(
    (catalogAttributes || []).map((a) => a.key || a.attribute_key).filter(Boolean),
  );
  const catalogNames = new Set(
    (catalogAttributes || [])
      .map((a) => String(a.name ?? '').trim().toLowerCase())
      .filter(Boolean),
  );

  return (draftAttributes || [])
    .filter((d) => String(d.name ?? '').trim() && (d.options || []).length > 0)
    .filter((d) => {
      const key = nameToAttributeKey(d.name);
      if (key && catalogKeys.has(key)) return false;
      if (catalogNames.has(String(d.name).trim().toLowerCase())) return false;
      return true;
    })
    .map((d) => ({
      key: nameToAttributeKey(d.name),
      name: String(d.name).trim(),
      values: (d.options || []).map((v) => String(v).trim()).filter(Boolean),
    }))
    .filter((d) => d.key && d.values.length > 0);
}

export function draftAttributesNeedExistingDefaults(catalogAttributes, draftAttributes, existingSkus) {
  if (!existingSkus?.length) return false;
  return detectNewDraftAttributes(catalogAttributes, draftAttributes).length > 0;
}
